import React, { useState, useEffect } from "react";
import axios from "axios";
import TopNav from "../../components/navigation/TopNav";
import StandardScreen, { ScreenContent } from "../../components/layout/StandardScreen";
import Stack from "../../components/layout/Stack";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import FormField from "../../components/forms/FormField";
import TextInput from "../../components/forms/TextInput";
import StatusMessage from "../../components/ui/StatusMessage";
import { useTranslation } from "react-i18next";

export default function AdminCreateBus() {
  const { t } = useTranslation();

  const [plateNumber, setPlateNumber] = useState("");
  const [notes, setNotes] = useState("");

  const [buses, setBuses] = useState([]);

  const [message, setMessage] = useState("");
  const [lastAction, setLastAction] = useState("");
  const [loadingBuses, setLoadingBuses] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem("token")}`;
    fetchBuses();
  }, []);

  const getErrorMessage = (err, fallbackKey) => {
    const detail = err?.response?.data?.detail;

    if (typeof detail === "string") return detail;

    if (Array.isArray(detail)) {
      return detail
        .map((item) => {
          const location = Array.isArray(item.loc) ? item.loc.join(".") : "";
          return location ? `${location}: ${item.msg}` : item.msg;
        })
        .filter(Boolean)
        .join(" | ");
    }

    return t(fallbackKey);
  };

  const translateStatus = (status) => {
    if (!status) return "-";
    return t(`admin_create_bus.status_values.${status}`, status);
  };

  const fetchBuses = async () => {
    setLoadingBuses(true);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/buses`);
      setBuses(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("❌ Failed to fetch buses", err);
      setBuses([]);
      setMessage("❌ " + t("admin_create_bus.messages.fetch_buses_failed"));
      setLastAction("fetch");
    } finally {
      setLoadingBuses(false);
    }
  };

  const resetForm = () => {
    setPlateNumber("");
    setNotes("");
  };

  const handleSubmit = async () => {
    setSubmitting(true);

    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL}/admin/buses`, {
        plate_number: plateNumber.trim() || null,
        notes: notes.trim() || null,
      });

      const createdBus = res.data?.bus || res.data;
      const createdBusCode = createdBus?.bus_code;
      const createdPlateNumber = createdBus?.plate_number;

      let successMessage = "✅ " + t("admin_create_bus.messages.success");

      if (createdBusCode) {
        successMessage += ` ${t("admin_create_bus.messages.created_bus_code")}: ${createdBusCode}.`;
      }

      if (createdPlateNumber) {
        successMessage += ` ${t("admin_create_bus.messages.created_plate_number")}: ${createdPlateNumber}.`;
      }

      setMessage(successMessage);
      setLastAction("submit");

      resetForm();
      fetchBuses();
    } catch (err) {
      console.error("❌ Error creating bus:", err);
      setMessage("❌ " + getErrorMessage(err, "admin_create_bus.messages.failure"));
      setLastAction("submit");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <StandardScreen
      background="default"
      topNav={<TopNav title={t("admin_create_bus.title")} mode="main" />}
    >
      <ScreenContent>
        <div style={{ maxWidth: 720, width: "100%", margin: "0 auto" }}>
          <Stack gap="4">
            <Card>
              <Stack gap="4">
                <h1 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 700 }}>
                  {t("admin_create_bus.title")}
                </h1>

                <FormField label={t("admin_create_bus.fields.plate_number.label")}>
                  <TextInput
                    value={plateNumber}
                    onChange={(e) => {
                      setPlateNumber(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_bus.fields.plate_number.placeholder")}
                  />
                </FormField>

                <FormField label={t("admin_create_bus.fields.notes.label")}>
                  <TextInput
                    value={notes}
                    onChange={(e) => {
                      setNotes(e.target.value);
                      setMessage("");
                      setLastAction("");
                    }}
                    placeholder={t("admin_create_bus.fields.notes.placeholder")}
                  />
                </FormField>

                <Button onClick={handleSubmit} disabled={submitting} fullWidth>
                  {submitting
                    ? t("admin_create_bus.status.submitting")
                    : lastAction === "submit" && message.startsWith("✅")
                    ? t("admin_create_bus.status.success_short")
                    : lastAction === "submit" && message.startsWith("❌")
                    ? `${t("admin_create_bus.status.error_short")}: ${message.replace("❌ ", "")}`
                    : t("admin_create_bus.actions.submit")}
                </Button>
              </Stack>
            </Card>

            <StatusMessage message={message} />

            <Card>
              <Stack gap="3">
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                  }}
                >
                  <h2 style={{ margin: 0, fontSize: "1.15rem", fontWeight: 700 }}>
                    {t("admin_create_bus.list.title")}
                  </h2>

                  <Button onClick={fetchBuses} disabled={loadingBuses}>
                    {loadingBuses
                      ? t("admin_create_bus.status.loading")
                      : t("admin_create_bus.actions.refresh")}
                  </Button>
                </div>

                {buses.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>
                    {loadingBuses
                      ? t("admin_create_bus.list.loading")
                      : t("admin_create_bus.list.empty")}
                  </div>
                ) : (
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_bus.list.columns.bus_code")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_bus.list.columns.plate_number")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_bus.list.columns.status")}
                          </th>
                          <th style={{ textAlign: "left", padding: "8px 0" }}>
                            {t("admin_create_bus.list.columns.notes")}
                          </th>
                        </tr>
                      </thead>

                      <tbody>
                        {buses.map((bus) => (
                          <tr key={bus.bus_id}>
                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {bus.bus_code || "-"}
                            </td>

                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {bus.plate_number || "-"}
                            </td>

                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {translateStatus(bus.status)}
                            </td>

                            <td style={{ padding: "8px 0", borderTop: "1px solid rgba(0,0,0,0.08)" }}>
                              {bus.notes || "-"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Stack>
            </Card>
          </Stack>
        </div>
      </ScreenContent>
    </StandardScreen>
  );
}